import React, { Component } from 'react';

import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { createInlineStyleButton } from 'draft-js-buttons';
import { stateToHTML } from 'draft-js-export-html';
import createImagePlugin from 'draft-js-image-plugin';
import { stateFromHTML } from 'draft-js-import-html';
import DraftEditor from 'draft-js-plugins-editor';
import createToolbarPlugin, { Separator } from 'draft-js-static-toolbar-plugin';
import { Field } from 'formik';
import _ from 'lodash';

import { htmlToText } from '@services/utils/StringService';

import css from './Editor.module.scss';
import ImageButton from './ImageButton/ImageButton';

const toolbarPlugin = createToolbarPlugin();

const { Toolbar } = toolbarPlugin;

const imagePlugin = createImagePlugin();

const plugins = [toolbarPlugin, imagePlugin];

const MAX_LENGTH = 10000;

type EditorProps = {
    value: string;
    editorName: string;
    editorId: string;
    editorImageUploadUrl: string;
    anchorRef?: string;
    editorPlaceholder: string;
    editorLabel?: string;
    customClass?: string;
    hasBlackIcons?: boolean;
    hasExtraSeparator?: boolean;
    onUpdateContent?: (string) => void;
};
type EditorState = {
    editor: boolean;
    editorState: any;
};

const boldEditing = {
    BOLD: {
        fontWeight: 'bold',
        fontFamily: `'Poppins-SemiBold', sans-serif`,
    },
};

// @ts-ignore
const BoldButton = createInlineStyleButton({
    style: 'BOLD',
    children: <img src={'/front-static/icons/placeholder/text-editor-bold.svg'} alt="bold" />,
});

// @ts-ignore
const ItalicButton = createInlineStyleButton({
    style: 'ITALIC',
    children: <img src={'/front-static/icons/placeholder/text-editor-italic.svg'} alt="italic" />,
});

// @ts-ignore
const UnderlineButton = createInlineStyleButton({
    style: 'UNDERLINE',
    children: <img src={'/front-static/icons/placeholder/text-editor-underline.svg'} alt="underline" />,
});

// @ts-ignore
const BoldButtonBlack = createInlineStyleButton({
    style: 'BOLD',
    children: <img src={'/front-static/icons/placeholder/text-editor-bold-black.svg'} alt="bold" />,
});

// @ts-ignore
const ItalicButtonBlack = createInlineStyleButton({
    style: 'ITALIC',
    children: <img src={'/front-static/icons/placeholder/text-editor-italic-black.svg'} alt="italic" />,
});

// @ts-ignore
const UnderlineButtonBlack = createInlineStyleButton({
    style: 'UNDERLINE',
    children: <img src={'/front-static/icons/placeholder/text-editor-underline-black.svg'} alt="underline" />,
});

export default class Editor extends Component<EditorProps, EditorState> {
    state = {
        editorState: EditorState.createEmpty(),
        editor: false,
    };

    private editor: any;

    componentDidMount() {
        const state = {
            editor: true,
            editorState: null,
        };

        state.editor = true;
        state.editorState = this.props.value
            ? EditorState.createWithContent(stateFromHTML(this.props.value))
            : EditorState.createEmpty();

        this.setState(state);
    }

    isEditorStateEmpty = (editorContent) => {
        const rawState = convertToRaw(editorContent);
        if (!rawState || _.isEmpty(rawState)) {
            return true;
        }
        const contentState = convertFromRaw(rawState);
        const hasImage = !_.isEmpty(rawState?.entityMap);

        return !((contentState.hasText() && contentState.getPlainText().trim() !== '') || hasImage);
    };

    onChange = (setFieldValue) => {
        return (editorState) => {
            this.setState({
                editorState,
            });

            if (editorState && !this.isEditorStateEmpty(editorState.getCurrentContent())) {
                setFieldValue(this.props.editorName, stateToHTML(editorState.getCurrentContent()));
                if (this.props.onUpdateContent) {
                    this.props.onUpdateContent(stateToHTML(editorState.getCurrentContent()));
                }
            } else {
                setFieldValue(this.props.editorName, null);
                if (this.props.onUpdateContent) {
                    this.props.onUpdateContent(null);
                }
            }
        };
    };

    getLengthOfSelectedText = () => {
        const currentSelection = this.state.editorState.getSelection();
        const isCollapsed = currentSelection.isCollapsed();

        let length = 0;

        if (!isCollapsed) {
            const currentContent = this.state.editorState.getCurrentContent();
            const startKey = currentSelection.getStartKey();
            const endKey = currentSelection.getEndKey();
            const startBlock = currentContent.getBlockForKey(startKey);
            const isStartAndEndBlockAreTheSame = startKey === endKey;
            const startBlockTextLength = startBlock.getLength();
            const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset();
            const endSelectedTextLength = currentSelection.getEndOffset();
            const keyAfterEnd = currentContent.getKeyAfter(endKey);
            if (isStartAndEndBlockAreTheSame) {
                length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
            } else {
                let currentKey = startKey;

                while (currentKey && currentKey !== keyAfterEnd) {
                    if (currentKey === startKey) {
                        length += startSelectedTextLength + 1;
                    } else if (currentKey === endKey) {
                        length += endSelectedTextLength;
                    } else {
                        length += currentContent.getBlockForKey(currentKey).getLength() + 1;
                    }
                    currentKey = currentContent.getKeyAfter(currentKey);
                }
            }
        }

        return length;
    };

    handleBeforeInput = () => {
        const currentContent = this.state.editorState.getCurrentContent();
        const currentContentLength = currentContent.getPlainText('').length;
        const selectedTextLength = this.getLengthOfSelectedText();

        if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
            return 'handled';
        }
    };

    handlePastedText = (pastedText) => {
        const currentContent = this.state.editorState.getCurrentContent();
        const currentContentLength = currentContent.getPlainText('').length;
        const selectedTextLength = this.getLengthOfSelectedText();

        if (currentContentLength + pastedText.length - selectedTextLength > MAX_LENGTH) {
            return 'handled';
        }
    };

    focus = () => {
        this.editor.focus();
    };

    render() {
        const { editor } = this.state;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return (
            <>
                {editor && (
                    <Field name={this.props.editorName} id={this.props.editorId}>
                        {({ form: { setFieldValue } }) => (
                            <div className={css.editor_wrapper}>
                                {this.props.editorLabel && (
                                    <label htmlFor={this.props.editorId} className={css.label}>
                                        {this.props.editorLabel}
                                    </label>
                                )}

                                <div
                                    className={`${css.editor} ${this.props.customClass || ''}`}
                                    onClick={this.focus}
                                    ref={this.props.anchorRef}
                                >
                                    <Toolbar>
                                        {(externalProps) => {
                                            return (
                                                <>
                                                    {this.props.hasBlackIcons ? (
                                                        <>
                                                            <BoldButtonBlack {...externalProps} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <BoldButton {...externalProps} />
                                                        </>
                                                    )}
                                                    {this.props.hasBlackIcons ? (
                                                        <>
                                                            <ItalicButtonBlack {...externalProps} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ItalicButton {...externalProps} />
                                                        </>
                                                    )}
                                                    {this.props.hasBlackIcons ? (
                                                        <>
                                                            <UnderlineButtonBlack {...externalProps} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UnderlineButton {...externalProps} />
                                                        </>
                                                    )}
                                                    <Separator {...externalProps} />
                                                    <ImageButton
                                                        {...externalProps}
                                                        id={`img_btn_${self.props.editorId}`}
                                                        imageUploadUrl={self.props.editorImageUploadUrl}
                                                        editorState={self.state.editorState}
                                                        hasBlackIcon={this.props.hasBlackIcons}
                                                        onChange={self.onChange(setFieldValue)}
                                                        modifier={imagePlugin.addImage}
                                                    />
                                                    {this.props.hasExtraSeparator && <Separator {...externalProps} />}
                                                </>
                                            );
                                        }}
                                    </Toolbar>
                                    <DraftEditor
                                        // @ts-ignore
                                        onChange={this.onChange(setFieldValue).bind(this)}
                                        editorState={this.state.editorState}
                                        plugins={plugins}
                                        placeholder={htmlToText(this.props.editorPlaceholder)}
                                        handleBeforeInput={this.handleBeforeInput}
                                        handlePastedText={this.handlePastedText}
                                        customStyleMap={boldEditing}
                                        ref={(element) => {
                                            this.editor = element;
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </Field>
                )}
            </>
        );
    }
}
